@layer refine.base {
  :host {
    --tab-item-transition: var(--transition-normal);
    --tab-item-radius: var(--rounded-lg);
    display: inline-flex;
    outline: none;
  }

  button {
    position: relative;
    display: inline-flex;
    gap: var(--size-2);
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: var(--_touch-target);
    padding: var(--tab-item-padding, var(--size-1-5) var(--size-4));
    font-family: inherit;
    font-size: var(--tab-item-font-size, var(--text-sm));
    font-weight: var(--font-medium);
    color: var(--tab-item-color, var(--text-color-secondary));
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    background: transparent;
    border: none;
    border-radius: var(--tab-item-radius);
    transition:
      background var(--tab-item-transition),
      color var(--tab-item-transition),
      box-shadow var(--tab-item-transition);
  }

  button:focus {
    outline: none;
  }

  :host(:focus-visible) button,
  button:focus-visible {
    outline: var(--border-2) solid var(--_theme-focus);
    outline-offset: 2px;
    box-shadow: 0 0 0 3px var(--_theme-shadow);
  }
}

@layer refine.variants {
  /* ─── solid / default ─── */
  :host,
  :host([variant='solid']) {
    --_hover-bg: var(--tab-item-hover-bg, var(--color-contrast-200));
    --_active-bg: var(--tab-item-active-bg, var(--color-contrast-0, var(--color-canvas)));
    --_active-color: var(--tab-item-active-color, var(--text-color-heading));
    --_active-shadow: var(--tab-item-active-shadow, var(--shadow-xs), var(--inset-shadow-xs));
  }

  /* ─── flat ─── */
  :host([variant='flat']) {
    --_hover-bg: var(--tab-item-hover-bg, var(--color-contrast-200));
    --_active-bg: var(--tab-item-active-bg, var(--color-contrast-0, var(--color-canvas)));
    --_active-color: var(--tab-item-active-color, var(--text-color-heading));
    --_active-shadow: var(--tab-item-active-shadow, var(--shadow-xs), var(--inset-shadow-xs));
  }

  /* ─── bordered ─── */
  :host([variant='bordered']) {
    --_hover-bg: var(--tab-item-hover-bg, var(--color-contrast-200));
    --_active-bg: var(--tab-item-active-bg, var(--color-contrast-0, var(--color-canvas)));
    --_active-color: var(--tab-item-active-color, var(--text-color-heading));
    --_active-shadow: var(--tab-item-active-shadow, var(--shadow-xs));
  }

  /* ─── ghost ─── */
  :host([variant='ghost']) {
    --_hover-bg: var(--tab-item-hover-bg, var(--color-contrast-100));
    --_active-bg: var(--tab-item-active-bg, var(--_theme-base));
    --_active-color: var(--tab-item-active-color, var(--_theme-contrast));
  }

  /* ─── glass ─── */
  :host([variant='glass']) {
    --_color: var(--tab-item-color, color-mix(in oklch, var(--color-secondary-contrast) 70%, transparent));
    --_hover-bg: var(--tab-item-hover-bg, color-mix(in oklch, var(--color-secondary-contrast) 10%, transparent));
    --_active-bg: var(--tab-item-active-bg, color-mix(in oklch, var(--color-secondary-contrast) 15%, transparent));
    --_active-color: var(--tab-item-active-color, var(--color-secondary-contrast));
    --_active-shadow: var(--tab-item-active-shadow, var(--shadow-xs), var(--inset-shadow-xs));
  }

  :host([variant='glass']) button {
    color: var(--_color);
    text-shadow: var(--text-shadow-2xs);
  }

  :host([variant='glass']) button:hover {
    color: var(--tab-item-active-color, var(--color-secondary-contrast));
  }

  /* ─── frost ─── */
  :host([variant='frost']) {
    --_hover-bg: var(--tab-item-hover-bg, color-mix(in oklch, var(--color-contrast) 10%, transparent));
    --_active-bg: var(--tab-item-active-bg, color-mix(in oklch, var(--color-canvas) 60%, transparent));
    --_active-color: var(--tab-item-active-color, var(--text-color-heading));
    --_active-shadow: var(--tab-item-active-shadow, var(--shadow-xs), var(--inset-shadow-xs));
  }

  :host([variant='frost']) button {
    color: var(--text-color-secondary);
  }

  /* ─── Shared interactive states (read per-variant --_* tokens) ─── */

  :host button:hover {
    color: var(--tab-item-active-color, var(--text-color-heading));
    background: var(--_hover-bg);
  }

  :host([active]) button {
    color: var(--_active-color);
    background: var(--_active-bg);
    box-shadow: var(--_active-shadow);
  }

  /* ─── sizes ─── */
  :host([size='sm']) {
    --tab-item-font-size: var(--text-xs);
    --tab-item-padding: var(--size-1) var(--size-3);
  }

  :host(:not([size])),
  :host([size='md']) {
    --tab-item-font-size: var(--text-sm);
    --tab-item-padding: var(--size-1-5) var(--size-4);
  }

  :host([size='lg']) {
    --tab-item-font-size: var(--text-base);
    --tab-item-padding: var(--size-2) var(--size-5);
  }

  /* ─── coarse pointer: promote one size tier for proportional scaling ─── */
  @media (pointer: coarse) {
    :host,
    :host([size='md']) {
      --tab-item-font-size: var(--text-base);
      --tab-item-padding: var(--size-2) var(--size-5);
    }

    :host([size='sm']) {
      --tab-item-font-size: var(--text-sm);
      --tab-item-padding: var(--size-1-5) var(--size-4);
    }
  }

  /* ─── disabled ─── */
  :host([disabled]) {
    pointer-events: none;
    opacity: 0.5;
  }

  @media (forced-colors: active) {
    /* Ensure focus ring is visible regardless of variant theme color */
    button:focus-visible {
      outline: 2px solid Highlight;
      box-shadow: none;
    }

    /* Active tab: box-shadow raised indicator is stripped; use outline inste
         ad so the selected tab remains distinguishable */
    :host([active]) button {
      outline: 2px solid Highlight;
      outline-offset: -2px;
    }
  }
}
